import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Pencil, Trash2, Lock, Loader2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { collateralApi } from '@/api/collateral';
import CollateralThumb from '@/components/CollateralThumb';

/**
 * Collateral template management — system templates (read-only) plus
 * authored custom templates. Marketing authors here; agents consume on the
 * Collateral (generate) page.
 */
export default function CollateralTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await collateralApi.listTemplates();
      setTemplates(data?.data?.items || []);
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? This can't be undone.`)) return;
    try {
      await collateralApi.deleteTemplate(t.id);
      toast.success('Template deleted');
      load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleDuplicate = async (t) => {
    try {
      const res = await collateralApi.duplicateTemplate(t.id);
      toast.success('Copied — opening the editor');
      navigate(`/collateral/templates/${res.data.id}/edit`);
    } catch (e) {
      toast.error('Could not duplicate template');
    }
  };

  return (
    <div className="space-y-6" data-testid="collateral-templates-page">
      <PageHeader
        title="Collateral templates"
        description="Author on-brand templates with dynamic fields. Agents generate from these on the Collateral page."
        actions={
          <Button onClick={() => navigate('/collateral/templates/new')} data-testid="new-template">
            <Plus className="h-4 w-4 mr-2" /> New template
          </Button>
        }
      />

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm p-4">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => (
            <Card key={t.id} data-testid={`template-${t.id}`} className="group overflow-hidden">
              <CollateralThumb
                templateId={t.id}
                animated={(t.formats || []).includes('mp4')}
                className="h-40 w-full border-b"
              />
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium truncate min-w-0">{t.name}</span>
                  {t.is_system
                    ? <Badge variant="outline" className="text-[10px] shrink-0"><Lock className="h-3 w-3 mr-1" />System</Badge>
                    : <Badge variant="secondary" className="text-[10px] shrink-0">Custom</Badge>}
                </div>
                {t.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>}
                <div className="flex items-center gap-2 mt-3 text-[11px] text-muted-foreground">
                  <Badge variant="outline" className="capitalize text-[10px]">{t.category}</Badge>
                  {t.artboard && <span>{t.artboard.width}×{t.artboard.height}</span>}
                  <span>{(t.layers || []).length} layers</span>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  {t.is_system ? (
                    <Button size="sm" variant="outline" onClick={() => handleDuplicate(t)} data-testid={`duplicate-${t.id}`}>
                      <Copy className="h-3.5 w-3.5 mr-1" /> Use as template
                    </Button>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/collateral/templates/${t.id}/edit`)} data-testid={`edit-${t.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDuplicate(t)} data-testid={`duplicate-${t.id}`} title="Duplicate">
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-destructive ml-auto" onClick={() => handleDelete(t)} data-testid={`delete-${t.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
